import { TranslationKey } from './../../../../types/translation/TranslationKey';

import React, { useContext, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Divider, Grid } from '@mui/material';
import { CancelButton, CreateButton } from '../../../../VentoryUI/components/common/Button';
import Modal from '../../../../VentoryUI/components/common/Modal';
import { t } from '../../../../types/translation/Translator';
import {
  AssetTransactionMutations,
  ProcessAssetTransactionResponse,
  ProcessAssetTransactionVariables,
} from '../../../../graphql/assetTransaction.graphql';
import { AssetTransactionContext } from '../../../../context/AssetTransactionContext';
import {
  AssetTransaction,
  AssetTransactionParentType,
  AssetTransactionType,
} from '../../../../types/asset_transaction/assetTransaction';
import TextInput from '../../Common/TextInput';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import Checkbox from '../../../../VentoryUI/components/common/Checkbox';
import { UserContext } from '../../../../context/UserContext';
import { CustomFieldEntityType } from '../../../../types/custom_field';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import CustomFieldInput from '../../Common/CustomFieldInput';
import { ContactContext } from '../../../../context/ContactContext';
import OwnershipAssetModal from './OwnershipAssetModal';

interface SignAssetModalInputProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  transaction?: AssetTransaction;
}

export default function SignAssetModal({ open, setOpen, transaction }: SignAssetModalInputProps) {
  if (!transaction) return null;

  const { assetTransactions, setAssetTransactions } = useContext(AssetTransactionContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { companyUsers } = useContext(UserContext);
  const { customFields } = useContext(CustomFieldContext);
  const { contacts } = useContext(ContactContext);

  const [approved, setApproved] = useState<boolean>(false);

  const [error, setError] = useState<string>('');

  const pmd = useMemo(() => {
    return productMasterData.get(transaction.product.productMasterDataId);
  }, [transaction]);

  const [process, { loading }] = useMutation<ProcessAssetTransactionResponse, ProcessAssetTransactionVariables>(
    AssetTransactionMutations.process,
    {
      onCompleted: res => {
        const transactions = res.processAssetTransaction;
        for (const transaction of transactions) {
          assetTransactions.set(transaction.id, new AssetTransaction(transaction));
        }
        setAssetTransactions(new Map(assetTransactions));
        handleClose();
      },
      onError: res => setError(res.message),
    },
  );

  const handleSign = async () => {
    try {
      await process({
        variables: {
          id: transaction.id,
        },
      });
    } catch (e) {
      setError(String(e));
    }
  };

  const handleClose = () => {
    setApproved(false);
    setOpen(false);
    setError('');
  };

  const title = useMemo(
    () =>
      transaction.type === AssetTransactionType.inbound
        ? t(TranslationKey.assetConfirmReceive).singular.label
        : t(TranslationKey.assetConfirmReturn).singular.label,
    [transaction],
  );

  const validationMessage = useMemo(
    () =>
      transaction.type === AssetTransactionType.inbound
        ? t(TranslationKey.assetApproveReceivalMessage).singular.label
        : t(TranslationKey.assetConfirmReturnMessage).singular.label,
    [transaction],
  );

  const displayMoveToUser =
    transaction.parentType === AssetTransactionParentType.reassign &&
    transaction.type === AssetTransactionType.outbound;

  const relevantCustomFields = useMemo(() => {
    return [...customFields.values()].filter(cf => cf.entityType === CustomFieldEntityType.asset);
  }, [customFields]);

  if (transaction.parentType === AssetTransactionParentType.ownership) {
    return <OwnershipAssetModal open={open} setOpen={setOpen} transaction={transaction} />;
  }

  return (
    <Modal open={open} error={error} onClose={handleClose} title={title}>
      <Grid container px={2} py={2}>
        <Grid item xs={12}>
          <Grid container rowSpacing={1} columnSpacing={1}>
            <Grid item xs={6}>
              <TextInput
                disabled={true}
                label={t(TranslationKey.assetName).singular.label}
                placeholder={t(TranslationKey.assetName).singular.label}
                value={productMasterData.get(transaction.product.productMasterDataId)?.productName}
                onChange={() => {}}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                disabled={true}
                label={t(TranslationKey.assetNumber).singular.label}
                placeholder={t(TranslationKey.assetNumber).singular.label}
                value={productMasterData.get(transaction.product.productMasterDataId)?.productNumber}
                onChange={() => {}}
              />
            </Grid>
            {transaction.product.serial ? (
              <Grid item xs={6}>
                <TextInput
                  disabled={true}
                  label={t(TranslationKey.serial).singular.label}
                  placeholder={t(TranslationKey.serial).singular.label}
                  value={transaction.product.serial}
                  onChange={() => {}}
                />
              </Grid>
            ) : null}
            <Grid item xs={6}>
              <TextInput
                type={'number'}
                disabled={true}
                label={t(TranslationKey.quantity).singular.label}
                placeholder={t(TranslationKey.quantity).singular.label}
                value={transaction.product.quantity.toString()}
                onChange={() => {}}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container columnSpacing={1}>
                {relevantCustomFields
                  .sort((a, b) => a.index - b.index)
                  .map(field => {
                    if (field.entityType !== CustomFieldEntityType.product) {
                      return (
                        <Grid item xs={6}>
                          <CustomFieldInput
                            key={field.id}
                            item={field}
                            users={companyUsers}
                            disabled={true}
                            value={transaction.product.customFields?.get(field.id)?.value || ''}
                            entity={transaction.product}
                            onChange={product => {}}
                            contacts={contacts}
                            productMasterData={productMasterData}
                          />
                        </Grid>
                      );
                    }
                  })}
              </Grid>
            </Grid>
            {displayMoveToUser ? (
              <Grid item xs={6}>
                <TextInput
                  disabled={true}
                  label={t(TranslationKey.returnToUser).singular.label}
                  placeholder={t(TranslationKey.returnToUser).singular.label}
                  value={
                    companyUsers.get(
                      [...assetTransactions.values()].find(
                        t => t.parentId === transaction.parentId && t.id !== transaction.id,
                      )?.location?.entityId || '',
                    )?.email
                  }
                  onChange={() => {}}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Checkbox value={approved} onChange={value => setApproved(value)} label={validationMessage} />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Grid container columnSpacing={1} justifyContent={'flex-end'}>
                <Grid item>
                  <CancelButton disabled={loading} onClick={handleClose} />
                </Grid>
                <Grid item>
                  <CreateButton
                    text={t(TranslationKey.sign).singular.label}
                    loading={loading}
                    disabled={!approved}
                    onClick={handleSign}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
}
